import { Box, Button, Divider, Icons, Stack } from '@cast/design-system';
import { ChartMetricsRow } from '@cast/features-cluster-ui';
import { FailedToLoad } from '@cast/shared-messages-ui';
import {
  jvmMetricToColor,
  jvmMetricToTitle,
  JvmHeapChart as JvmHeapChartComponent,
  useJvmChartData,
  WorkloadContainerSelect,
  useWorkloadSelectedContainer,
  makeCustomMetricsLink,
} from '@cast/split-me/features/cluster/workload-optimization';
import { useNavigate } from '@cast/infrastructure-organization';

import { InventoryResourceMetricBox } from '../../../_components/ui/InventoryResourceMetricBox';
import { InventoryResourceMetricBoxPair } from '../../../_components/ui/InventoryResourceMetricBoxPair';
import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { useInventoryWorkloadsTimeRangeSelection } from '../../../_hooks/useInventoryWorkloadsTimeRangeSelection';

export const JvmHeapChart = () => {
  const { timeZone } = useInventoryWorkloadsTimeRangeSelection();
  const { container } = useWorkloadSelectedContainer();
  const {
    workload,
    isFetching,
    error,
    refetch,
    chartData,
    toggleMetric,
    isMetricDisabled,
  } = useJvmChartData({ container, timeZone });
  const navigate = useNavigate();

  const isLoading = isFetching || !workload || !chartData;

  if (error && !isFetching) {
    return (
      <WidgetLayout
        title="JVM heap"
        testId="jvm-heap-chart"
        headerEndContent={<WorkloadContainerSelect />}
      >
        <FailedToLoad title="Failed to load JVM heap" refresh={refetch} />
      </WidgetLayout>
    );
  }

  return (
    <WidgetLayout
      title="JVM heap"
      testId="jvm-heap-chart"
      headerEndContent={
        <Stack direction="row" gap={8} alignItems="center">
          {workload && chartData.chartData.length > 0 && (
            <Button
              variant="text"
              size="lg"
              endIcon={<Icons.ArrowRight />}
              onClick={() =>
                navigate(
                  makeCustomMetricsLink({ clusterId: workload.clusterId }) +
                    `?metrics-explorer=${encodeURIComponent(
                      JSON.stringify({
                        workload: workload.id,
                        container: container?.name,
                      })
                    )}`
                )
              }
            >
              Go to custom metrics
            </Button>
          )}
          <WorkloadContainerSelect />
        </Stack>
      }
    >
      <Stack gap={12} height="100%">
        {workload && chartData && (
          <Stack
            direction="row"
            alignItems="flex-end"
            gap={24}
            sx={{ whiteSpace: 'nowrap' }}
          >
            <InventoryResourceMetricBoxPair
              currentDataKey="heapCommitted"
              currentValue={chartData.metrics.heapCommitted}
              recommendedDataKey="heapRecommended"
              recommendedValue={chartData.metrics.heapRecommended}
              unit="GiB"
              colorMap={jvmMetricToColor}
              titleMap={jvmMetricToTitle}
              toggleMetric={toggleMetric}
              isMetricDisabled={isMetricDisabled}
              size="m"
              alignItems="flex-end"
              gap={12}
              arrowWeight="regular"
            />
            <Divider orientation="vertical" flexItem />
            <ChartMetricsRow alwaysRow alignItems="flex-end" gap={16}>
              <InventoryResourceMetricBox
                dataKey="heapUsed"
                value={chartData.metrics.heapUsed}
                unit="GiB"
                colorMap={jvmMetricToColor}
                titleMap={jvmMetricToTitle}
                toggleMetric={toggleMetric}
                isMetricDisabled={isMetricDisabled}
                size="s"
              />
            </ChartMetricsRow>
          </Stack>
        )}
        <Box flex={1}>
          <JvmHeapChartComponent
            data={chartData?.chartData ?? []}
            isLoading={isLoading}
            hasError={!!error && !isFetching}
            refetch={refetch}
            isMetricDisabled={isMetricDisabled}
            timeZone={timeZone}
          />
        </Box>
      </Stack>
    </WidgetLayout>
  );
};
